import React, { useContext, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { AuthContext } from '@/context/auth-context'
import AccountIcon from '@/assets/icons/user-account.svg?react'
import CreditCardIcon from '@/assets/icons/credit-card.svg?react'
import SettingsIcon from '@/assets/icons/settings.svg?react'
import MessageDotsIcon from '@/assets/icons/message-dots.svg?react'
import LogOutIcon from '@/assets/icons/log-out.svg?react'
import { ArrowUpRight, Diamond, Tools, DropdownTriangle, EnterpriseOrganization, SyncProgress, FileClock } from '@/assets/icons'
import { accountPaths } from '@/routing/paths'
import { kDefaultLogoPath, kProduction } from '@/constants/constants-ui'
import { useLocation, useNavigate, useMatch } from 'react-router-dom'
import { useHubspotConversations } from '@/context/useHubspotConversations'
import signOutOfApp from '@/firebase/auth/sign-out'
import { SettingsEntrySource } from '@/analytics/enums/settings-entry.enums'
import { settingsLocationState } from '@/analytics/utils/settings-entry-analytics'
import { kLinkPrivacyPolicyUrl, kLinkTermsOfServiceUrl, kPaxtonHelpCenterUrl } from '@/constants/constants-links'
import { ClockStopwatch } from '@/assets/icons'
import { MenuContent, MenuItemGroup, MenuSeparator } from '@/components/menu/MenuContent'
import { MenuItem } from '@/components/menu/MenuItem'
import { Button } from '@/components/button/Button'
import { usePWAUpdate } from '@/context/pwa-update-context'
import { AppUpdateSource } from '@/analytics/enums/app-update.enums'
import Tooltip from '@/components/ui/tooltip/Tooltip'
import { useIsTruncated } from '@/components/ui/tooltip/useIsTruncated'
import { AccountMenuViewOrganizationCard } from '@/account/components/AccountMenuViewOrganizationCard'
import { SubscriptionStatus } from '@/firebase/auth/auth-jwt-schema'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { setReferralDialogVisible, setMobileSidebarOpen, setStripePortalDialogVisible, openSettingsModal } from '@/store/slices/ui-state.slice'
import { selectDisplayCostPerSeatInCents } from '@/account/store/selectors'
import { AccountActions } from '@/account/store/slice'
import { formatCentsToDollarsCompact } from '@/util/money'
import { RootState } from '@/store/store'
import { OrganizationRosterEntrySource } from '@/analytics/enums/organization.enums'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { getOrganizationAnalyticsId, resolveOrganizationRosterEntrySource, trackOrganizationEvent } from '@/analytics/utils/organization-analytics'
import { useOrganizationDetails } from '@/organizations/hooks/useOrganizationDetails'
import { useOrganizationDirectoryUsers } from '@/organizations/hooks/useOrganizationDirectoryUsers'
import { useRosterMembers } from '@/organizations/hooks/useRosterMembers'
import { MemberRosterModal, type BillingStats } from '@/organizations/views/MemberRosterModal'
import InviteMemberDialog from '@/organizations/views/InviteMemberDialog'
import { useOrgLogoUpload } from '@/organizations/hooks/useOrgLogoUpload'
import { capitalizeFirstLetters } from '@/util/string-utils'
import { isOrganizationRosterRequested, organizationRosterEntrySourceFromState } from '@/organizations/utils/organization-roster-navigation'
import LeaveOrganizationModalContainer from '@/organizations/views/LeaveOrganizationModalContainer'
import { PostHogDemoMenuItem } from './PostHogDemoMenuItem'
import { AccountMenuRevampContent } from './AccountMenuRevampContent'
import { useRuntimeFeatureFlag } from '@/feature-flags/useRuntimeFeatureFlag'
import { RuntimeFeatureFlag } from '@/feature-flags/definitions'
import { EMPTY_USER_IDENTITY } from '@/account/utils/resolve-user-identity'
import { useOcrQuotaSummary } from '@/ocr_quota/hooks/useOcrQuotaSummary'
import { OcrQuotaPolicy } from '@/ocr_quota/schemas'

type AccountMenuItemType = {
  name: string
  icon: React.FC<React.SVGProps<SVGSVGElement>>
  current: boolean
  onClick: () => void | Promise<void>
  hideFromAnon?: boolean
}

type SidebarAccountMenuProps = {
  /** Renders the avatar-only trigger for the contracted rail, with the menu anchored to its side */
  contracted?: boolean
}

const SidebarAccountMenu: React.FC<SidebarAccountMenuProps> = ({ contracted = false }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { toggleWidget } = useHubspotConversations()
  const { user, userAccountData, logoUrl, userIdentity: userIdentityFromContext } = useContext(AuthContext)
  // The real provider always supplies this; fall back to the inert identity so an inline mock that
  // omits it still renders (and we never recombine raw Firebase fields here).
  const userIdentity = userIdentityFromContext ?? EMPTY_USER_IDENTITY
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()
  const { updateAvailable, isApplying, applyUpdate } = usePWAUpdate()

  // Release 1 settings-revamp account menu. Treat the loading window as off so the current menu renders
  // without a flash; org roster features stay GA on both paths and are not gated by this flag.
  const { enabled: settingsRevamp, loading: settingsRevampLoading } = useRuntimeFeatureFlag(RuntimeFeatureFlag.SETTINGS_REVAMP_FOUNDATION)
  const showRevampMenu = settingsRevamp && !settingsRevampLoading
  const [menuOpen, setMenuOpen] = useState(false)
  const { summary: quotaSummary } = useOcrQuotaSummary({ enabled: menuOpen && Boolean(user && !user.isAnonymous) })
  const showUsage = quotaSummary?.is_trial === true && quotaSummary.policy === OcrQuotaPolicy.Cumulative

  // Member roster modal (opened from the org card in place of navigating to /dashboard/admin)
  const [rosterOpen, setRosterOpen] = useState(false)
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const [leaveOrganizationOpen, setLeaveOrganizationOpen] = useState(false)
  const pendingRosterEntrySourceRef = useRef(OrganizationRosterEntrySource.AccountMenu)
  const rosterOpenedTrackedKeyRef = useRef<string | null>(null)
  const { isBusy: isLogoBusy, uploadLogoFile, deleteLogo } = useOrgLogoUpload()

  // Reveal the full email via tooltip when the header email is visually clipped (small screens / long emails).
  const { ref: emailHeaderRef, isTruncated: isEmailHeaderTruncated } = useIsTruncated<HTMLSpanElement>()

  // Seat pricing (kept in the account store, sourced from Stripe)
  const seatCostCents = useAppSelector(selectDisplayCostPerSeatInCents)
  const subscriptionInterval = useAppSelector((s: RootState) => s.accountState.stripeSubscriptionInterval)
  // Account-menu layout splits on account type:
  //  - org accounts get the roster card (showOrgRoster)
  //  - non-org accounts get a slimmed header: email only, no plan tag — but Manage Subscription stays
  const showOrgRoster = !!userAccountData?.isOrgAccount
  const canViewSubscriptionInfo = userAccountData?.canViewStripeSubscriptionInfo

  useEffect(() => {
    if (contracted || !showOrgRoster || !isOrganizationRosterRequested(location.state)) return

    const rosterSource = resolveOrganizationRosterEntrySource(organizationRosterEntrySourceFromState(location.state))
    navigate(`${location.pathname}${location.search}${location.hash}`, { replace: true, state: null })

    if (showRevampMenu) {
      // Settings modal is already open (mounted in DashboardLayout); switch it to Members.
      dispatch(openSettingsModal({ section: 'members', organizationRosterEntrySource: rosterSource }))
    } else {
      pendingRosterEntrySourceRef.current = rosterSource
      setRosterOpen(true)
    }
  }, [contracted, dispatch, location.hash, location.pathname, location.search, location.state, navigate, showOrgRoster, showRevampMenu])

  useEffect(() => {
    if (!rosterOpen) {
      rosterOpenedTrackedKeyRef.current = null
      return
    }

    const organizationId = getOrganizationAnalyticsId(userAccountData)
    const organizationRosterEntrySource = pendingRosterEntrySourceRef.current
    const trackedKey = `${organizationId}:${organizationRosterEntrySource}`
    if (rosterOpenedTrackedKeyRef.current === trackedKey) return
    rosterOpenedTrackedKeyRef.current = trackedKey

    trackOrganizationEvent(trackEvent, AnalyticsEvent.OrganizationRosterOpened, {
      organizationId,
      organizationRosterEntrySource,
    })
  }, [rosterOpen, trackEvent, userAccountData])

  // Refresh payment details when roster modal opens to ensure billing stats are current
  useEffect(() => {
    if (rosterOpen && canViewSubscriptionInfo) {
      dispatch(AccountActions.refreshPaymentDetails({ forceRefresh: false }))
    }
  }, [rosterOpen, canViewSubscriptionInfo, dispatch])

  // DEV ONLY: Testing variables - set to true to test different states
  const devOverrides = {
    forceTrial: false, // Set to true to force trial state
    forceReferral: false, // Set to true to force show referral dialog
  }

  // Override values for testing if in development
  const subscriptionStatus = !kProduction && devOverrides.forceTrial ? SubscriptionStatus.TRIAL : userAccountData?.subscriptionStatus
  const userIsAnon = user?.isAnonymous
  const isPaidSubscriber = userAccountData?.subscriptionStatus === SubscriptionStatus.PAID
  const isOrgAccount = userAccountData?.isOrgAccount
  // Non-org account: slim header (email row) in place of the org card.
  const isNonOrg = !isOrgAccount
  const showReferral = !kProduction && devOverrides.forceReferral ? true : !userIsAnon && !isOrgAccount && isPaidSubscriber

  const isOrgAdmin = userAccountData?.isOrgAdmin
  // Only surface a real custom logo; fetchOrgLogoAsUrl falls back to the default Paxton path when none exists.
  const orgLogoUrl = logoUrl !== kDefaultLogoPath ? logoUrl : undefined

  // Org member data fetching — admin gets full details + invites, non-admin gets directory listing.
  // Gated on the feature flag so nothing fetches while the org roster UI is hidden.
  const {
    data: orgDetails,
    refetch: refetchOrgDetails,
    isError: orgDetailsIsError,
    isLoading: orgDetailsIsLoading,
  } = useOrganizationDetails({
    enabled: showOrgRoster && !!isOrgAdmin,
  })
  const {
    data: directoryUsers,
    isError: directoryUsersIsError,
    isLoading: directoryUsersIsLoading,
  } = useOrganizationDirectoryUsers({
    enabled: showOrgRoster && !isOrgAdmin,
  })
  // Only one query is ever enabled at a time (gated by isOrgAdmin); the other stays at isError/isLoading: false.
  const rosterIsError = orgDetailsIsError || directoryUsersIsError
  const rosterIsLoading = orgDetailsIsLoading || directoryUsersIsLoading

  // Refresh org member list on each modal open so accepted invites and role changes are reflected immediately
  useEffect(() => {
    if (rosterOpen && isOrgAdmin) {
      refetchOrgDetails()
    }
  }, [rosterOpen, isOrgAdmin, refetchOrgDetails])

  // Shape org users + pending invites (admin) or the directory (non-admin) into the roster list.
  const rosterMembers = useRosterMembers({ isOrgAdmin: !!isOrgAdmin, orgDetails, directoryUsers })
  // Actual joined members, excluding pending invites (distinct from purchased seats).
  const memberCount = rosterMembers.filter((member) => !member.isPending).length

  const billingStats: BillingStats = {
    paidSeats: userAccountData?.orgData?.seats ?? 0,
    pendingInviteCount: orgDetails?.organization.invites.length ?? 0,
    billingCadence: capitalizeFirstLetters(userAccountData?.billingCycle ?? '-'),
    seatCost: seatCostCents != null && subscriptionInterval ? `${formatCentsToDollarsCompact(seatCostCents)} / ${subscriptionInterval}` : '-',
  }

  // Add route matching
  const accountMatch = useMatch('/dashboard/account')
  const settingsMatch = useMatch('/dashboard/settings')
  const usageMatch = useMatch(accountPaths.usage)
  // This is for logged in users only
  if (!user || userIsAnon) {
    return null
  }

  // Menu elements
  const menuElements: AccountMenuItemType[] = [
    ...(showOrgRoster
      ? [] // No menu elements for org roster view replaced by the org card
      : [
          ...(!isOrgAccount
            ? [
                {
                  hideFromAnon: true,
                  name: 'Manage subscription',
                  icon: CreditCardIcon,
                  current: accountMatch !== null,
                  onClick: () => {
                    navigate('/dashboard/account')
                  },
                },
              ]
            : []),
        ]),
    {
      name: 'Settings',
      icon: SettingsIcon,
      current: settingsMatch !== null,
      onClick: () => {
        navigate('/dashboard/settings', { state: settingsLocationState(SettingsEntrySource.AccountMenu) })
      },
    },
    ...(showUsage ? [{ name: 'Usage', icon: FileClock, current: usageMatch !== null, onClick: () => navigate(accountPaths.usage) }] : []),
    {
      hideFromAnon: false,
      name: 'Chat with support',
      icon: MessageDotsIcon,
      current: false,
      onClick: () => {
        toggleWidget()
      },
    },
    {
      hideFromAnon: true,
      name: 'Sign out',
      icon: LogOutIcon,
      current: false,
      onClick: () => {
        signOutOfApp(navigate)
      },
    },
    ...(!kProduction
      ? [
          {
            name: 'Log token (dev only)',
            icon: Tools,
            current: false,
            onClick: async () => {
              const token = await user.getIdToken(true) // force refresh true
              console.log(token)
            },
          },
        ]
      : []),
  ].filter((item) => (userIsAnon && !item.hideFromAnon) || !userIsAnon)

  // Expanded, the trigger sits 5px below the footer top and 5px in from its right edge.
  // The update indicator is on the avatar, so this offset stays the same with or without an update.
  const menuPlacement = contracted
    ? { side: 'right' as const, align: 'end' as const, sideOffset: 8 }
    : showRevampMenu
    ? // Revamp opens above the 239px pill with a 2px overlap (Figma).
      { side: 'top' as const, align: 'end' as const, sideOffset: -2, alignOffset: -1 }
    : { align: 'end' as const, sideOffset: 4, alignOffset: -5 }

  return (
    <>
      <DropdownMenu.Root modal={false} open={menuOpen} onOpenChange={setMenuOpen}>
        {/* Contracted, the rail supplies the footer row and its top border, so the trigger is
            just the avatar circle. */}
        <div className={contracted ? '' : 'flex w-full items-center border-t py-1 pl-3 pr-1'}>
          <DropdownMenu.Trigger asChild>
            <button
              className={clsx(
                'group flex items-center rounded-full hover:bg-surface-hover focus:outline-hidden focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-brand-500',
                contracted ? 'h-8 w-8 justify-center' : 'min-w-0 flex-1 h-10 gap-x-2.5 pl-1 pr-3 py-2'
              )}
              aria-label={updateAvailable ? 'Account Menu, update available' : 'Account Menu'}
              type="button"
            >
              {/* Avatar precedence (Figma): an org account always shows its org identity (uploaded logo,
                  else the enterprise icon) — a member's personal initials must never replace it. Only
                  individual accounts show initials, falling back to the generic account icon.
                  Redesign style (white bg + border) applies to both org and non-org. */}
              <div className="relative flex-none">
                <div className="flex justify-center items-center w-8 h-8 rounded-full overflow-hidden bg-surface-emphasis border border-border-default">
                  {showOrgRoster ? (
                    orgLogoUrl ? (
                      <img src={orgLogoUrl} alt="" aria-hidden className="size-full object-contain p-1" />
                    ) : (
                      <EnterpriseOrganization className="w-5 h-5 text-brand-neutral-700" />
                    )
                  ) : userIdentity.initials ? (
                    <span className="text-brand-neutral-700 font-medium text-base">{userIdentity.initials}</span>
                  ) : (
                    <AccountIcon className="w-5 h-5 text-brand-neutral-700" />
                  )}
                </div>
                {updateAvailable && (
                  // Place the 8px dot center on the NE edge of the w-8 avatar: radius 16 × √2/2 ≈ 11.3px.
                  <span
                    aria-hidden="true"
                    data-testid="pwa-update-indicator"
                    className="pointer-events-none absolute left-1/2 top-1/2 size-2 translate-x-[calc(-50%+11.3px)] translate-y-[calc(-50%-11.3px)] rounded-full bg-brand-500 ring-2 ring-brand-neutral-50"
                  />
                )}
              </div>

              {!contracted && (
                <>
                  <span className="min-w-0 flex-grow truncate text-left text-xs font-medium leading-5">{userIdentity.navigationLabel}</span>
                  <DropdownTriangle className="h-4 w-4 shrink-0 text-text-primary" />
                </>
              )}
            </button>
          </DropdownMenu.Trigger>
        </div>

        <MenuContent className="w-[239px] border-border-strong" {...menuPlacement}>
          {showRevampMenu ? (
            <AccountMenuRevampContent
              email={userIdentity.email ?? ''}
              isOrgAccount={showOrgRoster}
              isOrgAdmin={!!isOrgAdmin}
              isPaxtonUserAccount={!!userAccountData?.isPaxtonUserAccount}
              orgName={userAccountData?.orgData?.name ?? ''}
              memberCount={memberCount}
              settingsSelected={settingsMatch !== null}
              usageSelected={usageMatch !== null}
              onUsage={showUsage ? () => navigate(accountPaths.usage) : undefined}
              // Flag on: the roster lives in the settings modal's Members section, so open that
              // instead of the standalone MemberRosterModal (which remains the flag-off path below).
              onOpenRoster={() =>
                dispatch(
                  openSettingsModal({
                    section: 'members',
                    settingsEntrySource: SettingsEntrySource.AccountMenu,
                    organizationRosterEntrySource: OrganizationRosterEntrySource.AccountMenu,
                  })
                )
              }
              onInviteMembers={() => setInviteDialogOpen(true)}
              // Revamp menu: open the settings modal in place of navigating to the legacy page.
              onSettings={() => dispatch(openSettingsModal({ settingsEntrySource: SettingsEntrySource.AccountMenu }))}
              onSupport={() => toggleWidget()}
              onSignOut={() => signOutOfApp(navigate)}
              onLogToken={
                !kProduction
                  ? async () => {
                      const token = await user.getIdToken(true) // force refresh true
                      console.log(token)
                    }
                  : undefined
              }
              onCloseMobileSidebar={() => dispatch(setMobileSidebarOpen(false))}
              updateAvailable={updateAvailable}
              isApplying={isApplying}
              onApplyUpdate={() => void applyUpdate({ source: AppUpdateSource.MenuItem })}
            />
          ) : (
            <>
              {/* Member roster view */}
              {showOrgRoster && (
                <AccountMenuViewOrganizationCard
                  orgName={userAccountData?.orgData?.name ?? ''}
                  memberCount={memberCount}
                  onViewOrganization={() => {
                    pendingRosterEntrySourceRef.current = OrganizationRosterEntrySource.AccountMenu
                    setRosterOpen(true)
                  }}
                  onInviteMembers={isOrgAdmin ? () => setInviteDialogOpen(true) : undefined}
                />
              )}

              {/* Non-org: trial banner stays on top; the email renders as a header row inside the
              menu list group below (matching the org card treatment), so no plan tag or separate block. */}
              {isNonOrg && subscriptionStatus === SubscriptionStatus.TRIAL && (
                <div className="flex items-center gap-1.5 px-4 py-2 bg-brand-50 border-b border-brand-100">
                  <ClockStopwatch className="w-5 h-5 text-brand-500" />
                  <span className="text-[14px] font-semibold leading-[1.429em] tracking-[-1.07%] text-brand-500">Free Trial</span>
                </div>
              )}

              {/* A non-org account has no header block above this group — its email row is the top of the
              list — so skip the leading divider that separates a header/card from the items. */}
              {!isNonOrg && <MenuSeparator />}
              <MenuItemGroup>
                <div className="flex items-center px-2 py-1">
                  <Tooltip title={isEmailHeaderTruncated ? userIdentity.email ?? '' : ''} placement="top">
                    <span className="flex w-full min-w-0 items-center">
                      <span ref={emailHeaderRef} className="min-w-0 flex-1 truncate text-sm font-medium leading-5 tracking-[-0.15px] text-text-tertiary">
                        {userIdentity.email}
                      </span>
                    </span>
                  </Tooltip>
                </div>
                {menuElements.map((item) => (
                  <MenuItem
                    key={item.name}
                    label={item.name}
                    icon={item.icon}
                    selected={!!item.current}
                    onSelect={() => {
                      item.onClick?.()
                      dispatch(setMobileSidebarOpen(false))
                    }}
                  />
                ))}
                {userAccountData?.isPaxtonUserAccount && <PostHogDemoMenuItem onSelect={() => dispatch(setMobileSidebarOpen(false))} />}
              </MenuItemGroup>

              <MenuSeparator />
              <MenuItemGroup>
                <MenuItem
                  label="Help Center"
                  trailingIcon={ArrowUpRight}
                  onSelect={() => {
                    window.open(kPaxtonHelpCenterUrl, '_blank')
                    dispatch(setMobileSidebarOpen(false))
                  }}
                />
                <MenuItem
                  label="Privacy Policy"
                  trailingIcon={ArrowUpRight}
                  onSelect={() => {
                    window.open(kLinkPrivacyPolicyUrl, '_blank')
                    dispatch(setMobileSidebarOpen(false))
                  }}
                />
                <MenuItem
                  label="Terms of Service"
                  trailingIcon={ArrowUpRight}
                  onSelect={() => {
                    window.open(kLinkTermsOfServiceUrl, '_blank')
                    dispatch(setMobileSidebarOpen(false))
                  }}
                />
              </MenuItemGroup>

              <MenuSeparator />
              <div>
                {showReferral && (
                  <div className="p-4" onClick={(e) => e.stopPropagation()}>
                    <div className="w-full bg-brand-50 border border-brand-100 rounded-[4px] p-3">
                      <div className="flex flex-col gap-2">
                        <p className="text-brand-neutral-700 text-xs leading-[1.333em] font-normal">
                          Invite others to join Paxton and earn 1 free month per referral.
                        </p>
                        <div className="flex justify-center items-center gap-1">
                          <Diamond className="w-4 h-4 text-brand-500" />
                          <button
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              dispatch(setMobileSidebarOpen(false))
                              dispatch(setReferralDialogVisible(true))
                              trackEvent(AnalyticsEvent.ReferralDialogOpened)
                            }}
                            className="text-brand-500 text-xs leading-[1.333em] font-semibold hover:opacity-80"
                          >
                            Get 1 Month Free
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className={clsx('bg-surface-subtle px-4 py-2', updateAvailable && 'flex items-center gap-2.5')}>
                  <span className={clsx('min-w-0 flex-1 text-[10.4px] leading-[1.077em] text-brand-neutral-700', !updateAvailable && 'block')}>
                    v{APP_VERSION}
                  </span>
                  {updateAvailable && (
                    // Same composition as Invite members: Item owns menu keyboard/select; Button is the visual.
                    <DropdownMenu.Item
                      disabled={isApplying}
                      onSelect={() => {
                        void applyUpdate({ source: AppUpdateSource.MenuItem })
                      }}
                      // Item owns focus; keep its own chrome clear and drive the button fill from highlighted.
                      className="group rounded p-0 outline-hidden data-[highlighted]:bg-transparent focus:bg-transparent"
                    >
                      <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        size="xs"
                        // Footer is brand-neutral-100, so bump hover/highlight one token darker to show.
                        className="hover:!bg-brand-neutral-200 active:!bg-brand-neutral-200 group-data-[highlighted]:!bg-brand-neutral-200"
                        leadingIcon={<SyncProgress className="h-3.5 w-3.5" />}
                        disabled={isApplying}
                      >
                        Update version
                      </Button>
                    </DropdownMenu.Item>
                  )}
                </div>
              </div>
            </>
          )}
        </MenuContent>
      </DropdownMenu.Root>

      {showOrgRoster && rosterOpen && userAccountData?.orgData?.name && (
        <MemberRosterModal
          open={rosterOpen}
          onClose={() => {
            pendingRosterEntrySourceRef.current = OrganizationRosterEntrySource.AccountMenu
            setRosterOpen(false)
            setLeaveOrganizationOpen(false)
            dispatch(setMobileSidebarOpen(false))
          }}
          orgName={userAccountData.orgData.name}
          orgLogoUrl={orgLogoUrl}
          currentUserId={user.uid}
          members={rosterMembers}
          billingStats={isOrgAdmin ? billingStats : undefined}
          onInviteMembers={isOrgAdmin ? () => setInviteDialogOpen(true) : undefined}
          onManageSubscription={userAccountData?.isOrgPayer ? () => dispatch(setStripePortalDialogVisible(true)) : undefined}
          onLogoFileSelected={isOrgAdmin ? uploadLogoFile : undefined}
          onLogoDelete={isOrgAdmin ? deleteLogo : undefined}
          isLogoBusy={isLogoBusy}
          showMemberMenu={isOrgAdmin ?? false}
          onMemberAction={refetchOrgDetails}
          showInviteMenu={isOrgAdmin ?? false}
          onInviteAction={refetchOrgDetails}
          isError={rosterIsError}
          isLoading={rosterIsLoading}
          onLeaveOrganization={userAccountData.isOrgPayer === false ? () => setLeaveOrganizationOpen(true) : undefined}
        />
      )}

      {showOrgRoster && leaveOrganizationOpen && userAccountData?.orgData?.name && (
        <LeaveOrganizationModalContainer visible organizationName={userAccountData.orgData.name} onClose={() => setLeaveOrganizationOpen(false)} stacked />
      )}

      <InviteMemberDialog
        stacked
        visible={inviteDialogOpen}
        onClose={() => {
          setInviteDialogOpen(false)
          // Only collapse the mobile sidebar when this dialog is the top surface;
          // when opened over the roster modal, leave the sidebar so the roster stays mounted.
          if (!rosterOpen) dispatch(setMobileSidebarOpen(false))
        }}
        onInviteSuccess={() => refetchOrgDetails()}
      />
    </>
  )
}

export default SidebarAccountMenu
